'use client';

import { HeartFilledIcon } from '@repo/icons';
import { CarouselItem } from '@repo/ui/carousel';
import { NoData } from '@repo/ui/no-data';
import { cn } from '@repo/utils/cn';
import { generateRandomNumber } from '@repo/utils/generate-random-number';
import { useTranslations } from 'next-intl';
import React, { type RefCallback, memo } from 'react';
import GameCard from '@/app/[locale]/(main)/casino/components/game-card';
import GamesCardsSkeleton from '@/components/common/skeletons/games-cards-skeleton';
import useCasinoGameClick from '@/hooks/useCasinoGameClick';
import useFavoritesGameClick from '@/hooks/useFavoritesGameClick';
import type { GameListResponseType, GameResponseType } from '@/lib/fetch/casino-games/client';
import { useAppStore } from '@/providers/AppStoreProvider';

type GamesCardsProps = {
  isCarousel?: boolean;
  isLoading?: boolean;
  data?: GameListResponseType;
  highlightedKey?: string;
  noDataClassName?: string;
  itemClassName?: string;
  isFetchingNextPage?: boolean;
  intersectionRef?: RefCallback<HTMLDivElement>;
  pageLimit?: number;
};

const GamesCards = ({
  isCarousel,
  data,
  highlightedKey,
  noDataClassName,
  itemClassName,
  isFetchingNextPage,
  isLoading,
  intersectionRef,
  pageLimit = 30,
}: GamesCardsProps) => {
  const t = useTranslations('casino');

  const { accessToken, balance } = useAppStore((state) => ({
    accessToken: state.accessToken,
    balance: state.balance,
  }));

  const { handleDemoPlay, handleRealPlay, dialogsRenderer } = useCasinoGameClick();

  const { handleFavoriteClick, favoriteCasinoGameIds } = useFavoritesGameClick();

  const onFavoriteButtonClick = React.useCallback(
    (game: GameResponseType) => {
      handleFavoriteClick(game.id!.toString());
    },
    [handleFavoriteClick]
  );

  const buttonsProps = React.useCallback(
    (game: GameResponseType) => ({
      primaryButton: {
        label: t('play-now'),
        props: {
          onClick: () => handleRealPlay(game),
          'data-testid': 'play-now',
        },
      },
      secondaryButton: {
        label: t('play-for-fun'),
        props: {
          onClick: () => handleDemoPlay(game),
          'data-testid': 'play-for-fun',
        },
      },
      ...(accessToken && {
        favoriteButtonProps: {
          onClick: () => onFavoriteButtonClick(game),
          'data-testid': 'favorite-button',
        },
      }),
    }),
    [accessToken, t, handleRealPlay, handleDemoPlay, onFavoriteButtonClick]
  );

  if (isLoading) {
    return (
      <GamesCardsSkeleton
        className={itemClassName}
        isCarousel={isCarousel}
        itemsCount={pageLimit / 2}
      />
    );
  }

  if (!data?.length && !isFetchingNextPage) {
    return (
      <NoData
        title={t('games-cards-no-data-title')}
        className={cn(
          'relative left-4 min-h-full w-screen overflow-hidden',
          !isCarousel && 'left-0 col-span-full w-full overflow-hidden',
          noDataClassName
        )}
      />
    );
  }

  const playingCountRenderer = (gameID?: string) => {
    const key = `playing-count-${gameID}`;
    return (
      <span
        suppressHydrationWarning
        className="truncate font-bold vt-colors-statuses-success-default vt-font-highlight-font-family vt-text-size-smaller"
      >
        {`• ${generateRandomNumber(key, 111, 555)} ${t('playing-count-label')}`}
      </span>
    );
  };

  const likesCountRenderer = (gameID?: string) => {
    const key = `favorite-count-${gameID}`;
    const isFavorite = gameID ? favoriteCasinoGameIds.includes(gameID) : false;
    return (
      <span
        suppressHydrationWarning
        className="flex w-full items-center justify-center truncate font-bold vt-font-highlight-font-family vt-text-size-smaller vt-body-text-darker"
      >
        <HeartFilledIcon className="size-4" />
        <span className="px-0.5" />
        {generateRandomNumber(key, 111, 999) + Number(isFavorite)}
      </span>
    );
  };

  const gamesCount = data?.length ?? 0;

  return (
    <>
      {dialogsRenderer()}

      {data?.map((game, index) => {
        const isLastItem = index === gamesCount - 1;

        const card = (
          <GameCard
            {...game}
            highlightedKey={highlightedKey}
            isFavoriteGame={favoriteCasinoGameIds.includes(game.id!.toString())}
            playingCountRenderer={() => playingCountRenderer(game.id!.toString())}
            likesCountRenderer={() => likesCountRenderer(game.id!.toString())}
            balance={balance}
            {...buttonsProps(game)}
          />
        );

        const refToAttach = isLastItem && !isFetchingNextPage ? intersectionRef : undefined;

        return isCarousel ? (
          <CarouselItem
            key={game.id ?? index}
            ref={refToAttach as RefCallback<HTMLDivElement>}
            className={cn('w-1/3 basis-auto sm:w-1/4 md:w-1/5 lg:w-1/6 xl:w-[13.3333%]', itemClassName)}
          >
            {card}
          </CarouselItem>
        ) : (
          <div
            key={game.id ?? index}
            ref={refToAttach}
            className="flex flex-col items-center justify-center"
          >
            {card}
          </div>
        );
      })}

      {isFetchingNextPage && (
        <GamesCardsSkeleton
          className={itemClassName}
          isCarousel={isCarousel}
          itemsCount={pageLimit / 2}
        />
      )}
    </>
  );
};

export default memo(GamesCards);
